import { useQueryClient } from "@tanstack/react-query";
import { useNoteStore, useSyncStore } from "@/stores/StoreProvider";
import type { Note } from "@/types/crm";
import { toast } from "sonner";
import { TIMELINE_KEY } from "@/hooks/useTimeline";
import { useAuth } from "@/contexts/AuthContext";

export const NOTES_KEY = "notes";

type EntityType = "contact" | "deal" | "task" | "project";

export function useNotes(entityType: EntityType, entityId: string) {
  const noteStore = useNoteStore();
  const syncStore = useSyncStore();
  // 100% local — attachment SAS URLs fetched JIT by <NoteAttachmentGallery>
  const data = entityId ? noteStore.getByEntity(entityType, entityId) as Note[] : [];
  return { data, isLoading: !syncStore.bootstrapComplete };
}

export function useCreateNote(entityType: EntityType, entityId: string) {
  const noteStore = useNoteStore();
  const queryClient = useQueryClient();
  const { userId } = useAuth();
  const create = async (content: string) => {
    try {
      const note = noteStore.create({ entityType, entityId, content, userId: userId ?? undefined });
      queryClient.invalidateQueries({ queryKey: [NOTES_KEY, entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: [TIMELINE_KEY, entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ["activity-notes"] });
      queryClient.invalidateQueries({ queryKey: ["deal-activity"] });
      queryClient.invalidateQueries({ queryKey: ["project-activity"] });
      toast.success("Note added");
      return note;
    } catch (error) {
      toast.error("Failed to add note");
      throw error;
    }
  };
  return { mutateAsync: create, mutate: (content: string) => { create(content); }, isPending: false };
}

export function useUpdateNote(entityType: EntityType, entityId: string) {
  const noteStore = useNoteStore();
  const queryClient = useQueryClient();
  const update = async ({ noteId, content }: { noteId: string; content: string }) => {
    try {
      noteStore.update(noteId, { content });
      queryClient.invalidateQueries({ queryKey: [NOTES_KEY, entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: [TIMELINE_KEY, entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ["activity-notes"] });
      queryClient.invalidateQueries({ queryKey: ["deal-activity"] });
      queryClient.invalidateQueries({ queryKey: ["project-activity"] });
      toast.success("Note updated");
    } catch (error) {
      toast.error("Failed to update note");
      throw error;
    }
  };
  return { mutateAsync: update, mutate: (args: { noteId: string; content: string }) => { update(args); }, isPending: false };
}

export function useDeleteNote(entityType: EntityType, entityId: string) {
  const noteStore = useNoteStore();
  const queryClient = useQueryClient();
  const del = async (noteId: string) => {
    try {
      noteStore.delete(noteId);
      queryClient.invalidateQueries({ queryKey: [NOTES_KEY, entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: [TIMELINE_KEY, entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ["activity-notes"] });
      queryClient.invalidateQueries({ queryKey: ["deal-activity"] });
      queryClient.invalidateQueries({ queryKey: ["project-activity"] });
      toast.success("Note deleted");
    } catch (error) {
      toast.error("Failed to delete note");
      throw error;
    }
  };
  return { mutateAsync: del, mutate: (noteId: string) => { del(noteId); }, isPending: false };
}
